import {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import VariableNote from '../common/VariableNote'
import UploadThemeImage from '../common/UploadThemeImage'
import {uploadFile} from 'utils/api/auth/mediaService'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {
 FestiveGreetingVariant,
 IUpdateFestiveGreetingPayload,
} from 'models/festiveGreeting'
import {
 createFestiveGreetingApi,
 deleteFestiveApi,
 deleteFestiveVariantApi,
 fetchFestiveGreetingDetails,
 updateFestiveApi,
} from 'utils/api/festiveGreetingService'
import FestiveTemplate from './FestiveTemplate'
import FestiveDialog from './FestiveDialog'
import {fetchLanguageList} from 'utils/api/auth/productPromotionService'
import {LanguageItem} from 'models/CreatePromotion'
import DeleteConfirmModal from '../common/DeleteConfirmModal'
import Preview from '../common/Preview'
import FestiveImageSection from './FestiveImageSection'

type IProps = {
 hidePartent: boolean
 setHideParent: (newState: boolean) => void
}

export interface IFormData {
 name?: string
 image?: string
 festive_greeting_variants?: FestiveGreetingVariant[]
}

const CreateFestiveGreeting = (props: IProps) => {
 const {t} = useTranslation()
 const {id} = useParams<{id: string}>()
 const originalImageRef = useRef<string | null>(null)
 const originalFormRef = useRef<IFormData | null>(null)
 const isEditMode = Boolean(id)
 const festiveGreetingId = id ? Number(id) : null
 const navigate = useNavigate()
 const [formState, setFormState] = useState<IFormData>({})
 const [showDelete, setShowDelete] = useState(false)
 const templates: FestiveGreetingVariant[] =
  formState.festive_greeting_variants ?? []
 const [imageLoader, setImageLoader] = useState(false)
 const [showDialog, setShowDialog] = useState(false)
 const [showPreview, setShowPreview] = useState(false)
 const [editRow, setEditRow] =
  useState<FestiveGreetingVariant | null>(null)
 const [languageMap, setLanguageMap] = useState<
  Record<string, LanguageItem>
 >({})
 const [selectedLang, setSelectedLang] = useState<
  string | null
 >(null)
 const [localImagePreview, setLocalImagePreview] = useState<
  string | null
 >(null)
 const languageVariants =
  formState.festive_greeting_variants?.map((v) => ({
   language_code: v.language_code,
   language:
    languageMap?.[v.language_code]?.language ||
    v.language_code,
  })) || []

 const canShowPreview = useMemo(() => {
  return (
   formState?.festive_greeting_variants &&
   formState?.festive_greeting_variants?.length > 0 &&
   !!formState?.image
  )
 }, [formState.festive_greeting_variants, formState.image])

 const selectedTemplate =
  formState.festive_greeting_variants?.find(
   (v) => v.language_code === selectedLang
  ) || null

 const VARIABLE_LIST = [
  {
   label: t(
    'festiveGreeting.variablesList.variable1.label'
   ),
   value: t(
    'festiveGreeting.variablesList.variable1.value'
   ),
  },
  {
   label: t(
    'festiveGreeting.variablesList.variable2.label'
   ),
   value: t(
    'festiveGreeting.variablesList.variable2.value'
   ),
  },
  {
   label: t(
    'festiveGreeting.variablesList.variable3.label'
   ),
   value: t(
    'festiveGreeting.variablesList.variable3.value'
   ),
  },
 ]

 const updateState = <K extends keyof IFormData>(
  name: K,
  value: IFormData[K]
 ) => {
  setFormState((prev) => ({
   ...prev,
   [name]: value,
  }))
 }

 const handleAdd = (
  data: Omit<FestiveGreetingVariant, 'id' | 'temp_id'>
 ) => {
  const newTemplate: FestiveGreetingVariant = {
   ...data,
   temp_id: Date.now(),
   is_temp: true,
  }

  updateState('festive_greeting_variants', [
   ...templates,
   newTemplate,
  ])
 }

 const isFormDirty = useMemo(() => {
  if (!isEditMode || !originalFormRef.current) return false

  const original = originalFormRef.current

  if (formState.name !== original.name) return true

  if (formState.image !== original.image) return true

  if (
   JSON.stringify(formState.festive_greeting_variants) !==
   JSON.stringify(original.festive_greeting_variants)
  ) {
   return true
  }

  return false
 }, [formState, isEditMode])

 const handleImageUpload = async (file: File) => {
  if (!file) return
  setImageLoader(true)
  const previewUrl = URL.createObjectURL(file)
  setLocalImagePreview(previewUrl)
  const formData = new FormData()
  formData.append('file', file)
  formData.append('file_type', 'image')
  formData.append('document_type', 'image')
  formData.append(
   'folder_name',
   'marketing/festive_greeting'
  )
  formData.append('bucket', 'public')

  const res = await uploadFile(formData)
  if (res?.filepath) {
   updateState('image', res.filepath)
  }

  setImageLoader(false)
 }

 const buildUpdatePayload =
  (): IUpdateFestiveGreetingPayload => {
   if (!festiveGreetingId || !formState.name) {
    throw new Error('Invalid state')
   }

   const existing = templates
    .filter((t) => !t.is_temp && t.id)
    .map((t) => ({
     id: t.id && t.id,
     language: t.language_code,
     template_text: t.template_text,
    }))

   const newlyAdded = templates
    .filter((t) => t.is_temp)
    .map((t) => ({
     language: t.language_code,
     template_text: t.template_text,
    }))

   return {
    id: festiveGreetingId,
    festival_name: formState.name,
    festive_greeting_variants: [...existing, ...newlyAdded],
    ...(formState.image !== originalImageRef.current
     ? {image_url: formState.image}
     : {}),
   }
  }

 const isFormValid = useMemo(() => {
  if (isEditMode) {
   return (
    isFormDirty &&
    ((!!formState.festive_greeting_variants &&
     formState.festive_greeting_variants.length > 0) ||
     !!formState.image)
   )
  }
  return (
   !!formState.name &&
   !!formState.image &&
   !!formState.festive_greeting_variants &&
   formState.festive_greeting_variants.length > 0
  )
 }, [formState])

 const buildPayload = () => {
  if (
   !formState.name ||
   !formState.image ||
   !formState.festive_greeting_variants
  )
   return {
    festival_name: '',
    image_url: '',
    festive_greeting_variants: [],
   }
  return {
   festival_name: formState.name,
   image_url: formState.image,
   template_variables: [
    'fpo_name',
    'fpo_centre_address',
    'aggregator_phone_number',
   ],
   festive_greeting_variants:
    formState.festive_greeting_variants.map((t) => ({
     language: t.language_code,
     template_text: t.template_text,
    })),
  }
 }

 const handleSubmit = async () => {
  if (!isFormValid) return
  const payload = buildPayload()
  const res = await createFestiveGreetingApi(payload)
  if (res.success) {
   navigate(-1)
  }
 }

 const handleUpdate = async () => {
  try {
   const payload = buildUpdatePayload()
   const res = await updateFestiveApi(payload)
   if (res?.data) navigate(-1)
  } catch {
   toast.error('Something went wrong')
  }
 }

 const loadDetails = async (
  festiveGreetingId: number,
  controller?: AbortController
 ) => {
  const res = await fetchFestiveGreetingDetails(
   festiveGreetingId,
   controller?.signal
  )
  if (!res) return
  const initialState: IFormData = {
   name: res.festival_name,
   image: res.image_url,
   festive_greeting_variants:
    res.festive_greeting_variants.map((v) => ({
     id: v.id,
     language_code: v.language,
     template_text: v.template_text,
     is_active: v.is_active,
     is_temp: false,
    })),
  }
  originalImageRef.current = res.image_url
  originalFormRef.current = initialState

  setFormState(initialState)
 }

 const handleDelete = (row: FestiveGreetingVariant) => {
  if (templates.length <= 1) {
   toast.error(t('createPromotion.warningText'))
   return
  }

  if (row.is_temp && row.temp_id) {
   updateState(
    'festive_greeting_variants',
    templates.filter((t) => t.temp_id !== row.temp_id)
   )
   return
  }

  if (!row.id) return

  setEditRow(row)
  void deleteFestiveVariantApi(row.id).then((success) => {
   if (success && festiveGreetingId) {
    void loadDetails(festiveGreetingId)
   }
  })
 }

 const handleDeleteFestive = async () => {
  const res = await deleteFestiveApi(
   festiveGreetingId as number
  )

  if (res.success) {
   setShowDelete(false)
   navigate(-1)
  }
 }

 const handleEdit = (
  data: Omit<FestiveGreetingVariant, 'id' | 'temp_id'>
 ) => {
  if (!editRow) return

  updateState(
   'festive_greeting_variants',
   templates.map((t) => {
    if (!t.is_temp && editRow.id && t.id === editRow.id) {
     return {...t, ...data}
    }
    if (
     t.is_temp &&
     editRow.temp_id &&
     t.temp_id === editRow.temp_id
    ) {
     return {...t, ...data}
    }

    return t
   })
  )

  setEditRow(null)
  setShowDialog(false)
 }

 useEffect(() => {
  const loadLanguages = async () => {
   const res = await fetchLanguageList()

   const map: Record<string, LanguageItem> = {}
   res.forEach((l) => {
    map[l.language_code] = l
   })

   setLanguageMap(map)
  }
  void loadLanguages()
 }, [])

 useEffect(() => {
  if (!festiveGreetingId) return

  const controller = new AbortController()

  void loadDetails(festiveGreetingId, controller)
  return () => controller.abort()
 }, [festiveGreetingId])

 useEffect(() => {
  props.setHideParent(false)
 }, [])

 useEffect(() => {
  if (languageVariants.length === 0) {
   setSelectedLang(null)
   return
  }

  const exists = languageVariants.some(
   (l) => l.language_code === selectedLang
  )

  if (!exists) {
   setSelectedLang(languageVariants[0].language_code)
  }
 }, [languageVariants, selectedLang])

 return (
  <>
   <p className='font-bold text-[20px] my-6'>
    {t('festiveGreeting.title')}
   </p>
   <VariableNote variables={VARIABLE_LIST} />

   <div
    className={`w-full md:w-1/3 rounded-xl border bg-[#F9FAFB] px-4 py-3 focus-within:border-black mt-6 mb-6
    ${formState.name ? 'bg-[#F9FAFB]' : 'bg-white'}
  `}
   >
    <p className='text-xs text-[#3F76FF] font-medium'>
     {t('festiveGreeting.festivalName')}
     <span className='text-red-500'>*</span>
    </p>

    <input
     value={formState.name || ''}
     onChange={(e) => updateState('name', e.target.value)}
     placeholder={t('festiveGreeting.enterFestivalName')}
     className='mt-1 w-full bg-transparent  outline-none'
    />
   </div>
   <p
    className=' mb-2'
    style={{fontSize: 16, fontWeight: '600'}}
   >
    {t('festiveGreeting.templateText')}
    <span className='text-red-500'>*</span>
   </p>
   <FestiveTemplate
    templates={templates}
    setEditRow={setEditRow}
    setShowDialog={setShowDialog}
    handleDelete={handleDelete}
    languageMap={languageMap}
   />
   <UploadThemeImage
    onImageSelect={handleImageUpload}
    loading={imageLoader}
    imageUrl={formState?.image}
    textFlag={false}
   />

   <div className='flex justify-between mt-8 gap-4'>
    {canShowPreview ? (
     <button
      onClick={() => setShowPreview(true)}
      className='px-6 py-2 rounded-lg border border-[#4E83E4] text-[#4E83E4] bg-[#F5FAFF]'
     >
      {t('preview.viewTemplate')}
     </button>
    ) : (
     <div></div>
    )}
    {!isEditMode && (
     <div className='flex justify-end  gap-4'>
      <button
       onClick={handleSubmit}
       className={`
      px-6 py-2 rounded-lg font-medium transition
      ${
       !isFormValid
        ? 'bg-[#C5CED6] text-white cursor-not-allowed'
        : 'bg-[#5E7CE2] text-white'
      }
    `}
      >
       {t('festiveGreeting.publishTemplate')}
      </button>
     </div>
    )}
    {isEditMode && (
     <div className='flex gap-4'>
      <button
       onClick={() => {
        setShowDelete(true)
       }}
       className='px-6 py-2 rounded-lg bg-[#CC293F] text-white'
      >
       {t('confirmation.delete')}
      </button>
      <button
       onClick={handleUpdate}
       className={`px-6 py-2 rounded-lg text-white
    ${
     !isFormValid
      ? 'bg-[#C5CED6] cursor-not-allowed'
      : 'bg-[#4E83E4]'
    }`}
      >
       {t('confirmation.update')}
      </button>
     </div>
    )}
   </div>

   {showDelete && (
    <DeleteConfirmModal
     title={t('festiveGreeting.deleteFestiveTemplate')}
     description={t('festiveGreeting.removeMessage')}
     onConfirm={handleDeleteFestive}
     onCancel={() => {
      setShowDelete(false)
     }}
    />
   )}
   {showDialog && (
    <FestiveDialog
     editData={editRow}
     onClose={() => {
      setEditRow(null)
      setShowDialog(false)
     }}
     templates={templates}
     onSubmit={editRow ? handleEdit : handleAdd}
    />
   )}
   <Preview
    open={showPreview}
    onClose={() => {
     if (languageVariants.length > 0) {
      setSelectedLang(languageVariants[0].language_code)
     } else {
      setSelectedLang(null)
     }
     setShowPreview(false)
    }}
    languageVariants={languageVariants}
    selectedLang={selectedLang}
    setSelectedLang={setSelectedLang}
   >
    <FestiveImageSection
     imageUrl={localImagePreview || formState.image}
     festivalName={formState.name}
     selectedTemplate={selectedTemplate}
     fpoName='Trade Name'
     address='Address Line 1, City - Pincode'
     phone='Name - Phone Number'
    />
   </Preview>
  </>
 )
}

export default CreateFestiveGreeting
